import React, { useContext } from "react";
import { Fade } from "react-reveal";
import ExperienceCard from "../components/experienceCard/ExperienceCard";
import AchievementCard from "../components/achievementCard/AchievementCard";
import StyleContext from "../contexts/StyleContext";
import "./Page.scss";

const Experiences = () => {
  const { isDark } = useContext(StyleContext);

  const experiences = [
    {
      company: "Broad Institute",
      role: "Software Engineer",
      date: "2023 - Present",
      companylogo: require("../assets/images/broad_logo.jpg"),
      desc: "Working on cutting-edge genomic data analysis and bioinformatics tools.",
      descBullets: [
        "Developed scalable data processing pipelines for genomic analysis",
        "Collaborated with research teams to implement machine learning algorithms",
        "Contributed to open-source bioinformatics tools and libraries"
      ]
    },
    {
      company: "AbbVie",
      role: "Machine Learning Intern",
      date: "Jun 2024 - Aug 2024",
      companylogo: require("../assets/images/abbvie-logo.jpeg"),
      desc: "Applied machine learning to accelerate pharmaceutical research and drug development",
      descBullets: [
        "Designed supervised ML models and data pipelines to streamline drug discovery workflows",
        "Developed predictive analytics to process pharmaceutical datasets and evaluate drug efficacy",
        "Deployed models on a Python web app with Oracle database integration"
      ]
    },
    {
      company: "Mayo Clinic",
      role: "AI/ML Research Intern",
      date: "Jun 2021 - Aug 2021",
      companylogo: require("../assets/images/mayo-clinic.jpeg"),
      desc: "Applied deep learning to analyze motor function in Parkinson's disease research",
      descBullets: [
        "Built ML models to predict limb markers from rat treadmill videos",
        "Automated training and prediction workflows for 100+ videos",
        "Developed Python pipelines to extract and visualize gait KPIs for induced Parkinsonian lesions"
      ]
    }
  ];

  const achievements = [
    {
      title: "Apple Entrepreneurship Camp Attendee",
      description: "Selected from a global applicant pool for the research iPhone app TrainYourEyes101, designed to strengthen the eye–brain connection for personalized vision enhancement",
      bullets: [
        "Collaborated with Apple engineers to enhance features and optimize App Store readiness",
        "Developed TrainYourEyes101 iPhone app for personalized vision training"
      ],
      image: null,
      imageAlt: "",
      footer: []
    },
    {
      title: "National Merit Scholar",
      description: "8,200 students from the 1.6 million that take the PSAT exam are awarded a $2,500 scholarship based on their PSAT score as well as academic and personal performance",
      bullets: [
        "Selected from 1.6 million PSAT test-takers nationwide",
        "Awarded $2,500 scholarship based on academic excellence and personal achievement"
      ],
      image: null,
      imageAlt: "",
      footer: []
    }
  ];

  return (
    <div className="page-container">
      <Fade bottom duration={1000} distance="20px">
        <div className="page-header">
          <h1 className="page-title">Experiences</h1>
        </div>
      </Fade>
      
      <Fade bottom duration={1000} distance="20px" delay={200}>
        <div className="page-content">
          <div className="experience-section">
            <div className="experience-cards-div">
              {experiences.map((experience, index) => (
                <ExperienceCard
                  key={index}
                  isDark={isDark}
                  cardInfo={{
                    company: experience.company,
                    desc: experience.desc,
                    date: experience.date,
                    companylogo: experience.companylogo,
                    role: experience.role,
                    descBullets: experience.descBullets
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </Fade>
      
      <Fade bottom duration={1000} distance="20px" delay={400}>
        <div className="page-content">
          <div className="projects-section">
            <h1 className="page-title">Big Projects</h1>
            <div className="projects-cards-div">
                <div className="project-card">
                  <div className="project-header">
                    <img 
                      src={require("../assets/images/fior-logo.jpeg")} 
                      alt="FiOR Medical Logo" 
                      className="project-logo"
                    />
                    <div className="project-title-section">
                      <h3>FiOR Medical</h3>
                      <p className="project-role">Co-Founder, Software Lead</p>
                      <p className="project-date">Mar 2021 - Dec 2022</p>
                    </div>
                  </div>
                  <ul className="project-bullets">
                    <li>Prototyped a sensor-based fire-prevention device in Arduino to reduce operating room fires</li>
                    <li>Plotted the relationship of oxygen sensor readings and surgical oxygen levels</li>
                    <li>Filed a provisional patent and conducted design verification testing at Johns Hopkins Hospital</li>
                  </ul>
                  <div className="project-tech">
                    <span className="tech-tag">Arduino</span>
                    <span className="tech-tag">C++</span>
                    <span className="tech-tag">IoT</span>
                    <span className="tech-tag">Medical Devices</span>
                    <span className="tech-tag">Patent</span>
                  </div>
                </div>
              
              <div className="project-card">
                <div className="project-header">
                  <div className="project-title-section">
                    <h3>Convolutional Neural Network for ASL Classification</h3>
                    <p className="project-date">Jan 2022 - May 2022</p>
                  </div>
                </div>
                <p>Built a deep learning model to classify ASL hand symbols into 26 English letters, achieving 99% accuracy through preprocessing, data augmentation, and feature engineering.</p>
                <div className="project-tech">
                  <span className="tech-tag">PyTorch</span>
                  <span className="tech-tag">Scikit-learn</span>
                  <span className="tech-tag">Python</span>
                </div>
              </div>
              
              <div className="project-card">
                <div className="project-header">
                  <div className="project-title-section">
                    <h3>Knee Implantable Device</h3>
                    <p className="project-role">Design Team Leader, JHU</p>
                    <p className="project-date">Jan 2022 – May 2023</p>
                  </div>
                </div>
                <ul className="project-bullets">
                  <li>Led an 8-person BME team to design an implantable sensor-based device for monitoring strain in knee reconstructive procedures</li>
                  <li>Collaborated with Dr. Jason Hammond and MedStar Health to align engineering design with clinical needs</li>
                  <li>Directed engineering solutions, guided medical research, and cultivated a strong interdisciplinary team culture</li>
                </ul>
                <div className="project-tech">
                  <span className="tech-tag">Biomedical Engineering</span>
                  <span className="tech-tag">Medical Device Design</span>
                  <span className="tech-tag">Clinical Collaboration</span>
                </div>
              </div>
              
              <div className="project-card">
                <div className="project-header">
                  <div className="project-title-section">
                    <h3>Computational Cardiology Lab</h3>
                    <p className="project-role">Student Researcher, JHU</p>
                    <p className="project-date">Mar 2020 – May 2021</p>
                  </div>
                </div>
                <ul className="project-bullets">
                  <li>Analyzed patient-specific 3D virtual heart models using OpenCARP to determine optimal ablation sites for arrhythmia treatment</li>
                  <li>Developed 2D cardiac models to study the impact of repolarization gradients on ventricular tachycardia morphology</li>
                </ul>
                <div className="project-tech">
                  <span className="tech-tag">Computational Cardiology</span>
                  <span className="tech-tag">Biomedical Modeling</span>
                  <span className="tech-tag">Clinical Research</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fade>
      
      <Fade bottom duration={1000} distance="20px" delay={600}>
        <div className="page-content">
          <div className="achievements-section">
            <h1 className="page-title">Achievements And Certifications 🏆</h1>
            <div className="achievements-cards-div">
              {achievements.map((achievement, index) => (
                <AchievementCard
                  key={index}
                  isDark={isDark}
                  cardInfo={{
                    title: achievement.title,
                    description: achievement.description,
                    bullets: achievement.bullets,
                    image: achievement.image,
                    imageAlt: achievement.imageAlt,
                    footer: achievement.footer
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </Fade>
    </div>
  );
};

export default Experiences;
